import React, { useEffect, useState } from 'react';
import {
  Button,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  TextField,
  Autocomplete,
} from '@mui/material';

import useCustomerRequest from '../../../requests/CustomerRequest';

export default function UpgradeTrialCustomerForm({
  showUpgradeTrialModal,
  hideModal,
  errorHandler,
  refreshCustomer,
  customerId,
}) {
  const [formData, setFormData] = useState({
    provider_id: '',
    tariff_plan_id: '',
  });
  const [validated, setValidated] = useState(false);
  const [providers, setProviders] = useState([]);
  const [tariffPlans, setTariffPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // State objects for the react select dropdown values, not the form data.
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedTariffPlan, setSelectedTariffPlan] = useState(null);
  const { getAllProviders, getTariffPlans, upgradeTrialCustomer } =
    useCustomerRequest();

  useEffect(() => {
    const fetchProviders = async () => {
      const providers = await getAllProviders(errorHandler);
      setProviders(providers || []);
    };
    fetchProviders();
  }, []);

  // Onchange handler for the provider dropdown.
  const providerChange = async (selected) => {
    setSelectedProvider(selected);
    setSelectedTariffPlan(null);
    setTariffPlans([]);
    setFormData({
      tariff_plan_id: '',
      provider_id: selected ? selected.value : '',
    });
    const tariffPlans = await getTariffPlans(selected.value, errorHandler);
    setTariffPlans(tariffPlans);
  };

  // Onchange handler for the tariff plan dropdown.
  const tariffPlanChange = (selected) => {
    setSelectedTariffPlan(selected);
    setFormData({
      ...formData,
      tariff_plan_id: selected ? selected.value : '',
    });
  };

  const handleSubmit = async (event) => {
    setIsLoading(true);
    event.preventDefault();
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.stopPropagation();
    } else {
      console.log(formData);
      const body = {
        provider_id: formData.provider_id,
        tariff_plan_id: formData.tariff_plan_id,
        customer_id: customerId,
      };
      const response = await upgradeTrialCustomer(body, errorHandler);
      if (response.status === 200) {
        hideModal();
        refreshCustomer();
      }
    }
    setValidated(true);
    setIsLoading(false);
  };

  const generateProviderDropdown = () => {
    return (
      <Autocomplete
        id="provider-select"
        options={providers}
        getOptionLabel={(option) => option.provider_name}
        value={
          providers.find((p) => p.provider_id === formData.provider_id) || null
        }
        onChange={(_, newValue) => {
          providerChange({
            value: newValue?.provider_id || '',
            label: newValue?.provider_name,
          });
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Provider"
            required
            error={validated && !formData.provider_id}
            helperText={!formData.provider_id ? 'Please select a provider' : ''}
          />
        )}
        loading={providers.length === 0}
      />
    );
  };

  const generateTariffPlanDropdown = () => {
    return (
      <Autocomplete
        id="tariff-plan-select"
        options={tariffPlans}
        getOptionLabel={(option) => option.tariff_plan_name}
        value={
          tariffPlans.find(
            (t) => t.tariff_plan_id === formData.tariff_plan_id
          ) || null
        }
        onChange={(_, newValue) => {
          tariffPlanChange({
            value: newValue?.tariff_plan_id || '',
            label: newValue?.tariff_plan_name,
          });
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Tariff Plan"
            required
            error={validated && !formData.tariff_plan_id}
            helperText={
              !formData.tariff_plan_id ? 'Please select a tariff plan' : ''
            }
          />
        )}
        loading={tariffPlans.length === 0}
        disabled={!formData.provider_id}
      />
    );
  };

  return (
    <Dialog
      open={showUpgradeTrialModal}
      onClose={hideModal}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullWidth
      maxWidth="md"
    >
      <DialogTitle id="alert-dialog-title">Upgrade Trial</DialogTitle>
      <DialogContent>
        <Box
          component="form"
          noValidate
          validated={validated}
          onSubmit={handleSubmit}
        >
          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              marginTop: '1rem',
            }}
          >
            <FormControl required>{generateProviderDropdown()}</FormControl>
            <FormControl required>
              {generateTariffPlanDropdown()}
              <FormHelperText error></FormHelperText>
            </FormControl>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        {isLoading ? (
          <Button
            type="submit"
            className="btn add-new-template float-right"
            disabled
          >
            <CircularProgress size={20} />
          </Button>
        ) : (
          <Button
            type="submit"
            className="btn add-new-template float-right"
            onClick={handleSubmit}
          >
            Submit Upgrade
          </Button>
        )}
        <Button
          onClick={hideModal}
          className="btn add-new-template float-right"
        >
          Cancel Upgrade
        </Button>
      </DialogActions>
    </Dialog>
  );
}
